import type { ISchema } from '@yimoka/store';

type Translate = (key: string) => string;

const formLayoutProps = {
  layout: 'vertical',
  size: 'large',
  style: {
    position: 'relative',
    width: 368,
    height: 300,
  },
};

const underlinedInputProps = {
  size: 'large',
  variant: 'underlined',
  style: {
    height: 54,
    width: '100%',
  },
};

function fieldStyle(top: number) {
  return {
    position: 'absolute',
    left: 0,
    top,
    width: 368,
    marginBottom: 0,
  };
}

const agreementStyle = {
  position: 'absolute',
  left: 0,
  top: 310,
  width: 327,
  height: 22,
  marginBottom: 0,
};

const submitWrapperStyle = {
  position: 'absolute',
  left: 0,
  top: 164,
  width: 368,
  height: 44,
};

const submitProps = {
  block: true,
  size: 'large',
  style: {
    height: 44,
    fontSize: 16,
    fontWeight: 500,
  },
};

export function createPasswordLoginSchema(t: Translate): ISchema {
  return {
    type: 'object',
    properties: {
      form: {
        type: 'void',
        'x-component': 'AgreementLoginForm',
        'x-component-props': {
          ...formLayoutProps,
          store: '{{$store}}',
        },
        properties: {
          phonePrefix: {
            type: 'string',
            default: '86',
            'x-hidden': true,
          },
          name: {
            type: 'string',
            required: true,
            'x-decorator': 'FormItem',
            'x-decorator-props': {
              style: fieldStyle(0),
            },
            'x-component': 'Input',
            'x-component-props': {
              ...underlinedInputProps,
              autoComplete: 'username',
              placeholder: t('Account:login.accountPlaceholder'),
            },
            'x-validator': [{ required: true, message: t('Account:login.accountRequired') }],
          },
          password: {
            type: 'string',
            required: true,
            'x-decorator': 'FormItem',
            'x-decorator-props': {
              style: fieldStyle(80),
            },
            'x-component': 'Input.Password',
            'x-component-props': {
              ...underlinedInputProps,
              autoComplete: 'current-password',
              placeholder: t('Account:login.passwordPlaceholder'),
            },
            'x-validator': [{ required: true, message: t('Account:login.passwordRequired') }],
          },
          agreement: {
            type: 'boolean',
            'x-decorator': 'FormItem',
            'x-decorator-props': {
              style: agreementStyle,
            },
            'x-component': 'AgreementCheckbox',
            'x-component-props': {
              className: 'account-agreement-checkbox',
            },
          },
          submitWrapper: {
            type: 'void',
            'x-decorator': 'div',
            'x-decorator-props': { style: submitWrapperStyle },
            'x-component': 'Submit',
            'x-component-props': {
              ...submitProps,
              'data-testid': 'account-login-submit',
              store: '{{$store}}',
              children: t('Account:login.submit'),
            },
          },
        },
      },
    },
  };
}

export function createPhoneLoginSchema(t: Translate): ISchema {
  return {
    type: 'object',
    properties: {
      form: {
        type: 'void',
        'x-component': 'AgreementLoginForm',
        'x-component-props': {
          ...formLayoutProps,
          store: '{{$store}}',
        },
        properties: {
          phonePrefix: {
            type: 'string',
            default: '86',
            required: true,
            'x-hidden': true,
          },
          phone: {
            type: 'string',
            required: true,
            'x-decorator': 'FormItem',
            'x-decorator-props': {
              style: fieldStyle(0),
            },
            'x-component': 'PhoneInput',
            'x-component-props': {
              ...underlinedInputProps,
              autoComplete: 'tel-national',
              placeholder: t('Account:login.phonePlaceholder'),
              type: 'tel',
            },
            'x-validator': [{ required: true, message: t('Account:login.phoneRequired') }],
          },
          code: {
            type: 'string',
            required: true,
            'x-decorator': 'FormItem',
            'x-decorator-props': {
              style: fieldStyle(80),
            },
            'x-component': 'Input',
            'x-component-props': {
              ...underlinedInputProps,
              autoComplete: 'one-time-code',
              placeholder: t('Account:login.codePlaceholder'),
              style: {
                ...underlinedInputProps.style,
                paddingRight: 116,
              },
            },
            'x-validator': [{ required: true, message: t('Account:login.codeRequired') }],
          },
          captcha: {
            type: 'void',
            'x-decorator': 'div',
            'x-decorator-props': {
              style: {
                position: 'absolute',
                right: 0,
                top: 80,
                width: 120,
                height: 54,
              },
            },
            // 验证码发送依赖当前表单手机号，复用旧项目基础组件并走当前实体 fetchFn。
            'x-component': 'PhoneCaptchaButton',
            'x-component-props': {
              className: 'account-captcha-button',
              'data-testid': 'account-phone-captcha',
              text: t('Account:login.sendCode'),
            },
          },
          agreement: {
            type: 'boolean',
            'x-decorator': 'FormItem',
            'x-decorator-props': {
              style: agreementStyle,
            },
            'x-component': 'AgreementCheckbox',
            'x-component-props': {
              className: 'account-agreement-checkbox',
            },
          },
          submitWrapper: {
            type: 'void',
            'x-decorator': 'div',
            'x-decorator-props': { style: submitWrapperStyle },
            'x-component': 'Submit',
            'x-component-props': {
              ...submitProps,
              'data-testid': 'account-login-submit',
              store: '{{$store}}',
              children: t('Account:login.submit'),
            },
          },
        },
      },
    },
  };
}
